/**
 * Meta5 Trading Hub — landing page that splits the trading platform into
 * two modes: AI Trading and Manual Trading. Matches Dashboard styling —
 * DashboardHeader + PageBreadcrumb.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot, HandMetal, Zap } from 'lucide-react';

import DashboardHeader from '../components/DashboardHeader';
import PageBreadcrumb from '../components/PageBreadcrumb';
import { useLanguage } from '../context/LanguageContext';
import { usePerms } from '../permissions/PermissionsContext';

const TILES = [
  {
    id: 'ai',
    // Name kept the same for both AR/EN — always reads LTR so "606" stays LEFT.
    name: '606 MT5 AI TRADING',
    descAr: 'بوت التداول الذكي — تحليل الأسواق، توليد الإشارات، وتنفيذ آلي لصفقات مدروسة',
    descEn: 'AI trading bot — market analysis, signal generation, and automated intelligent trades',
    icon: Bot,
    accent: '#d4af37',
    route: '/meta5-trading/ai',
    bullets: [
      { ar: 'ذكاء اصطناعي متعدد المزودين', en: 'Multi-provider AI' },
      { ar: 'استراتيجيات SMC جاهزة', en: 'Ready-made SMC strategies' },
      { ar: 'إدارة تلقائية للمخاطر', en: 'Automated risk management' },
    ],
  },
  {
    id: 'manual',
    name: '606 MT5 MANUAL TRADING',
    descAr: 'أوامر يدوية موحّدة على 6 حسابات دفعة واحدة — نفس اللوت والـ TP/SL',
    descEn: 'Unified manual orders across 6 accounts at once — same lot, TP and SL',
    icon: HandMetal,
    accent: '#22c55e',
    route: '/meta5-trading/manual',
    bullets: [
      { ar: '6 حسابات متزامنة', en: '6 synchronized accounts' },
      { ar: 'أوامر فورية ومعلّقة', en: 'Market & pending orders' },
      { ar: 'إغلاق جماعي بضغطة زر', en: 'One-click close-all' },
    ],
  },
];

export default function Meta5TradingHub() {
  const navigate = useNavigate();
  const { visiblePages, canViewRoute } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white" data-testid="meta5-trading-hub">
      <DashboardHeader
        showBackButton
        onBack={() => navigate('/dashboard')}
      />

      <PageBreadcrumb items={[
        { label: isAr ? '606 MT5 AI AND MANUAL TRADING' : '606 MT5 AI AND MANUAL TRADING' },
      ]} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" dir={isAr ? 'rtl' : 'ltr'}>
        {/* Page title — always LTR so "606" reads first on the LEFT */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-black text-white" dir="ltr">
            606 MT5 AI AND MANUAL TRADING
          </h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">
            {isAr ? 'اختر وضع التداول' : 'Choose trading mode'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {visiblePages(TILES, 'route').map((tile, idx) => {
            const Icon = tile.icon;
            return (
              <motion.button
                key={tile.id}
                onClick={() => navigate(tile.route)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                whileHover={{ y: -4 }}
                dir="ltr"
                className="text-left rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-black/40 hover:from-white/[0.08] hover:border-white/25 transition-all p-6 group relative overflow-hidden"
                data-testid={`hub-tile-${tile.id}`}
              >
                {/* accent glow */}
                <div
                  className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-25"
                  style={{ background: tile.accent }}
                />

                {/* Icon row */}
                <div className="flex items-start justify-between relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center border"
                    style={{
                      background: `${tile.accent}22`,
                      borderColor: `${tile.accent}55`,
                    }}
                  >
                    <Icon className="h-7 w-7" style={{ color: tile.accent }} />
                  </div>
                  <Zap
                    className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: tile.accent }}
                  />
                </div>

                {/* Title — LTR so "606" always sits on the left */}
                <h3
                  className="mt-5 text-xl font-black text-left"
                  style={{ color: tile.accent }}
                  dir="ltr"
                >
                  {tile.name}
                </h3>

                {/* Description — respects language direction */}
                <p
                  className="mt-2 text-sm text-[#A1A1AA] leading-relaxed"
                  dir={isAr ? 'rtl' : 'ltr'}
                >
                  {isAr ? tile.descAr : tile.descEn}
                </p>

                <div className="mt-5 flex flex-wrap gap-1.5" dir={isAr ? 'rtl' : 'ltr'}>
                  {tile.bullets.map((b, i) => (
                    <span
                      key={i}
                      className="text-[10px] px-2.5 py-1 rounded-full border border-white/15 bg-white/[0.03] text-white/70"
                    >
                      {isAr ? b.ar : b.en}
                    </span>
                  ))}
                </div>

                <div
                  className="mt-6 flex items-center gap-2 text-sm font-bold"
                  style={{ color: tile.accent }}
                  dir={isAr ? 'rtl' : 'ltr'}
                >
                  {isAr ? 'ادخل' : 'Enter'}
                  <span className="opacity-70 group-hover:translate-x-1 transition-transform inline-block">
                    {isAr ? '←' : '→'}
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
